// ============================================
// APPLICATION - Venta Hooks (con soporte offline)
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ventaRepository } from '../../infrastructure/http/venta.repository';
import { ventasRepository } from '../../infrastructure/http/ventas.repository';
import { outboxService } from '../../infrastructure/offline/outbox';
import { queryKeys } from '../query-keys';
import { useUIStore } from '../../state/ui.store';
import { useVentaStore } from '../../state/venta.store';
import type { CreateVentaDto, VentaFiltros } from '../../domain/dtos';
import { handleApiError } from '../../infrastructure/http/axios-client';

/**
 * Hook para listar ventas
 */
export const useVentas = (filtros?: VentaFiltros) => {
  return useQuery({
    queryKey: queryKeys.ventas.list(filtros),
    queryFn: () => ventaRepository.list(filtros),
    staleTime: 30 * 1000,
  });
};

/**
 * Hook para obtener venta por ID
 */
export const useVenta = (id: string) => {
  return useQuery({
    queryKey: queryKeys.ventas.detail(id),
    queryFn: () => ventaRepository.getById(id),
    enabled: Boolean(id),
  });
};

/**
 * Hook para crear venta (con soporte offline)
 */
export const useCreateVenta = () => {
  const queryClient = useQueryClient();
  const isOnline = useUIStore((state) => state.isOnline);
  const limpiarVenta = useVentaStore((state) => state.limpiarVenta);

  return useMutation({
    mutationFn: async (dto: CreateVentaDto) => {
      if (isOnline) {
        // Online: enviar directamente
        return await ventaRepository.create(dto);
      } else {
        // Offline: guardar en outbox
        const localId = await outboxService.addVenta(dto);
        return {
          id: localId,
          localId,
          syncStatus: 'pending' as const,
          ...dto,
          numero: `LOCAL-${localId.slice(0, 8)}`,
          subtotal: dto.detalles.reduce(
            (sum, d) => sum + d.cantidad * d.precioUnitario,
            0
          ),
          impuestos: 0,
          total: dto.detalles.reduce(
            (sum, d) => sum + d.cantidad * d.precioUnitario,
            0
          ) - (dto.descuento || 0),
          estado: 'PENDING' as const,
          vendedorId: '',
          vendedorNombre: '',
          almacenId: '',
          detalles: dto.detalles.map((d) => ({
            ...d,
            productoCodigo: '',
            productoNombre: '',
            subtotal: d.cantidad * d.precioUnitario,
          })),
          creadoEn: new Date().toISOString(),
          actualizadoEn: new Date().toISOString(),
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ventas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: ['mi-negocio'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.productos.lists() });
      limpiarVenta();
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para obtener ventas pendientes del outbox
 */
export const useOutboxVentas = () => {
  return useQuery({
    queryKey: queryKeys.ventas.outbox(),
    queryFn: outboxService.getPendingVentas,
    refetchInterval: 30 * 1000, // Cada 30s
  });
};

/**
 * Hook para sincronizar ventas offline
 */
export const useSyncVentas = () => {
  const queryClient = useQueryClient();
  const isOnline = useUIStore((state) => state.isOnline);

  return useMutation({
    mutationFn: async () => {
      if (!isOnline) {
        throw new Error('Sin conexión a internet');
      }

      const pendientes = await outboxService.getPendingVentas();
      const resultados = {
        sincronizadas: 0,
        fallidas: 0,
        errores: [] as Array<{ localId: string; error: string }>,
      };

      for (const venta of pendientes) {
        try {
          await outboxService.markAsSyncing(venta.id!);
          await ventaRepository.create(venta.dto);
          await outboxService.markAsSynced(venta.id!);
          resultados.sincronizadas++;
        } catch (error) {
          const apiError = handleApiError(error);
          await outboxService.markAsError(venta.id!, apiError.message);
          resultados.fallidas++;
          resultados.errores.push({
            localId: venta.localId,
            error: apiError.message,
          });
        }
      }

      await outboxService.updateLastSync();
      return resultados;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ventas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: ['mi-negocio'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.productos.all });
    },
  });
};

/**
 * Hook para cancelar venta
 */
export const useCancelVenta = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      ventaRepository.cancel(id, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ventas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: ['mi-negocio'] });
    },
    onError: (error) => {
      throw handleApiError(error);
    },
  });
};

/**
 * Hook para crear venta desde POS
 */
export const useCrearVentaPos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: import('../../domain/dtos').CrearVentaPosDto) =>
      ventasRepository.crearVenta(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ventas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: ['mi-negocio'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.productos.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.all });
    },
  });
};
